import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Listen, Method, Prop, Watch } from '@stencil/core';
import { SharedDirectionService } from '../../global/init-services';
import { getDataOnFromTheme, WorkbenchTheme } from '../../shared/themes';
import { FlyInPosition } from './fly-in.types';

/**
 * @slot - Contents of the fly-in
 **/
@Component({
  tag: 'wb-fly-in',
  styleUrl: 'fly-in.scss',
  shadow: true,
})
export class FlyInComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /**
   * Colour scheme
   * @deprecated Use `data-on` attribute instead
   * */
  @Prop({ reflect: true })
  public theme: WorkbenchTheme;

  /** side from which the component flies in  */
  @Prop({ reflect: true })
  public from: FlyInPosition = 'end';

  /** set to true to make component fly in */
  @Prop({ reflect: true, mutable: true })
  public open: boolean = false;

  /** set to true to hide backdrop when fly-in is open */
  @Prop({ reflect: true })
  public hideBackdrop: boolean = false;

  /** Makes fly-in have recommended padding */
  @Prop({ reflect: true })
  public padded: boolean = false;

  /** @internal if present sets the direction of the element to rtl */
  @Prop({ reflect: true, mutable: true })
  public isRtl: boolean = false;

  /** Fly-in aria-label, description provided to assistive technologies */
  @Prop({ reflect: true })
  public flyInAriaLabel!: string;

  /** Event emitted when fly-in closes */
  @Event({ bubbles: true })
  public wbflyinclose!: EventEmitter<null>;

  /**
   * @deprecated Use `wbflyinclose` instead
   * Event emitted when fly-in closes
   */
  @Event({ bubbles: true })
  public wbclose!: EventEmitter<null>;

  /** toggles the fly-out between open and closed */
  @Method()
  public async toggle(): Promise<void> {
    this.open = !this.open;
  }

  @Listen('wbdirchange', { target: 'document' })
  public updateDirection() {
    this.isRtl = SharedDirectionService.isRtl;
  }

  @Watch('open')
  public async handleOpenChange(): Promise<void> {
    if (!this.open) {
      this.wbflyinclose.emit();
      this.wbclose.emit();
    }
  }

  public connectedCallback(): void {
    this.updateDirection();

    // The code below is to avoid breaking changes when users were adding padding to the host wb-fly-in directly
    // DEFER TODO: Remove in the next major version
    if (!this.padded) {
      const elementPadding = getComputedStyle(this.el).padding;
      if (elementPadding !== '0px') {
        this.el.style.setProperty('--padding', elementPadding);
      }
    }
  }

  public render(): JSX.Element {
    return (
      <Host class="wb-fly-in" role="dialog" aria-label={this.flyInAriaLabel} data-on={getDataOnFromTheme(this.el, this.theme)}>
        <div class="inner" tabindex="0">
          <slot />
        </div>
      </Host>
    );
  }
}
